import { useId, useState } from 'react';
import { BarChart3, FlaskConical, Table2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ncoder/ui';
import { cn } from '@/lib/utils';
import { OrgAdminEmptyState, OrgAdminErrorState } from '../OrgAdminStates';

/**
 * The frame every chart in the Analysis view sits in.
 *
 * It owns everything that is not the plot: title, one-line explanation of what the chart answers,
 * the measure control, loading/empty/error states, the synthetic-data badge, and the table view.
 * Charts stay pure plots — which is what makes them reusable and testable.
 *
 * Two decisions worth calling out:
 *
 * **The table view is not a nicety.** A chart's values must be reachable without hovering — for
 * screen readers, for anyone who cannot use a pointer precisely, and for the reader who just wants
 * the number. Rather than bolt on a hidden `<table aria-hidden>` nobody maintains, the toggle swaps
 * the plot for a real, visible, sorted table of exactly the rows charted. It is genuinely useful, so
 * it stays correct.
 *
 * **Refetching dims, it does not skeleton.** Changing the window keeps the previous render at
 * reduced opacity. A chart that disappears and returns reads as broken; one that fades reads as
 * loading, and the reader keeps their place.
 */

export interface ChartCardProps {
  title: string;
  /** What question this chart answers. One line, plain language, always visible. */
  description: string;
  /** Measure selector or similar. Rendered in the header, right-aligned. */
  controls?: React.ReactNode;
  /**
   * Set when the plotted values did not come from the API. Renders a visible badge — the reader
   * must never have to guess whether a chart is real.
   */
  syntheticNote?: string;
  isLoading?: boolean;
  /** A refetch is in flight but previous data is on screen. */
  isFetching?: boolean;
  isError?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /**
   * Shape of the loading placeholder.
   *
   * Matches the chart it stands in for: `columns` for the trend, `rows` for a ranked horizontal bar
   * chart. A skeleton that predicts a different layout than the one that arrives makes the load feel
   * like a jump even when nothing actually moves.
   */
  skeleton?: 'columns' | 'rows';
  /** True when there is genuinely nothing to plot. */
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** The accessible table of the same rows. Omit to hide the toggle. */
  table?: React.ReactNode;
  /** Footer content — a link to exact figures, a caveat. */
  footer?: React.ReactNode;
  /**
   * Stable identifier emitted as `data-oa-card`, so the QA sweep can assert on a specific card's
   * structure rather than on its prose. Copy changes should not break a test that is really about
   * whether the synthetic-data gate held.
   */
  testId?: string;
  children: React.ReactNode;
  className?: string;
}

export function ChartCard({
  title,
  description,
  controls,
  syntheticNote,
  isLoading,
  isFetching,
  isError,
  error,
  onRetry,
  skeleton = 'columns',
  isEmpty,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  table,
  footer,
  testId,
  children,
  className,
}: ChartCardProps) {
  const [showTable, setShowTable] = useState(false);
  const bodyId = useId();
  const titleId = `${bodyId}-title`;

  const showToggle = !!table && !isLoading && !isError && !isEmpty;

  return (
    <section
      aria-labelledby={titleId}
      data-oa-card={testId}
      className={cn('oa-panel flex min-w-0 flex-col', className)}
    >
      <header className="oa-toolbar flex flex-wrap items-start justify-between gap-x-3 gap-y-2 py-2.5">
        <div className="min-w-0 flex-1 basis-40">
          <div className="flex items-center gap-2">
            <h3 id={titleId} className="oa-title truncate">
              {title}
            </h3>
            {syntheticNote && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* `tabIndex` so the explanation is reachable by keyboard, not hover-only. */}
                  <span
                    tabIndex={0}
                    // Machine-checkable marker: the QA sweep asserts on this attribute to prove the
                    // production build never renders synthetic data, and that a development build
                    // never renders it unlabelled.
                    data-oa-synthetic="true"
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-strong"
                  >
                    <FlaskConical className="h-2.5 w-2.5" aria-hidden />
                    Sample data
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[280px] text-xs">
                  {syntheticNote}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="oa-meta mt-0.5">{description}</p>
        </div>

        {/*
          Wraps rather than `shrink-0`. A four-option measure control plus the Values button is wider
          than a phone-width card, and a `shrink-0` cluster simply overflowed the card and was
          clipped — the last option and the toggle became unreachable. Wrapping lets the control take
          its own line, which is the honest answer at that width.
        */}
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          {controls}
          {showToggle && (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              aria-pressed={showTable}
              aria-controls={bodyId}
              className="oa-interactive inline-flex h-7 items-center gap-1.5 rounded-md border border-border/70 px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            >
              {showTable ? (
                <BarChart3 className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <Table2 className="h-3.5 w-3.5" aria-hidden />
              )}
              {showTable ? 'Chart' : 'Values'}
            </button>
          )}
        </div>
      </header>

      <div id={bodyId} className="min-w-0 flex-1">
        {isLoading ? (
          <div
            role="status"
            aria-busy="true"
            aria-label={`Loading ${title}`}
            // `h-[200px]`, not `min-h-`: the column bars are sized as a PERCENTAGE of this box, and a
            // percentage height does not resolve against a parent that only has a min-height — so
            // every bar computed to zero and the whole loading state rendered blank.
            className={cn(
              'p-[var(--oa-gutter)]',
              skeleton === 'columns' ? 'flex h-[200px] items-end gap-2' : 'flex flex-col gap-2.5',
            )}
          >
            <span className="sr-only">Loading {title}</span>
            {/* Deterministic proportions, so screenshots stay comparable between runs. */}
            {[52, 74, 41, 88, 63, 35, 70].map((size, index) =>
              skeleton === 'columns' ? (
                <div key={index} className="oa-skeleton flex-1" style={{ height: `${size}%` }} />
              ) : (
                // A ranked chart's row: the category label, then the bar.
                <div key={index} className="flex items-center gap-3">
                  <div className="oa-skeleton h-2.5 shrink-0" style={{ width: 72 }} />
                  <div className="oa-skeleton h-3.5" style={{ width: `${size}%` }} />
                </div>
              ),
            )}
          </div>
        ) : isError ? (
          <OrgAdminErrorState
            title={`Couldn't load ${title.toLowerCase()}`}
            error={error}
            onRetry={onRetry}
            className="min-h-[200px]"
          />
        ) : isEmpty ? (
          // Tighter than the plot it replaces: an empty chart should not reserve a chart's worth of
          // blank space, especially when the emptiness is a standing condition rather than a
          // this-window one.
          <OrgAdminEmptyState
            icon={BarChart3}
            title={emptyTitle}
            description={emptyDescription}
            className="min-h-[156px]"
          />
        ) : showTable ? (
          <div className="max-h-[320px] overflow-auto">{table}</div>
        ) : (
          <div
            className={cn(
              'p-[var(--oa-gutter)] pt-3 transition-opacity duration-200 motion-reduce:transition-none',
              isFetching && 'opacity-55',
            )}
            // The plot is decorative to a screen reader — the table view carries the data. Marking
            // it hidden stops SVG internals being read out as a wall of numbers.
            aria-hidden={!!table}
          >
            {children}
          </div>
        )}
      </div>

      {footer && !isLoading && !isError && (
        <div className="oa-footer-rail flex items-center justify-between gap-3 py-1.5">{footer}</div>
      )}
    </section>
  );
}

/**
 * The table rendered behind a ChartCard's "Values" toggle.
 *
 * Uses the module's own `.oa-table` chrome, so a chart's table view is visually the same object as
 * the Usage tab's tables rather than a third table style.
 */
export function ChartValueTable({
  caption,
  columns,
  rows,
}: {
  caption: string;
  columns: Array<{ label: string; align?: 'left' | 'right' }>;
  rows: Array<{ key: string; cells: React.ReactNode[] }>;
}) {
  return (
    <table className="oa-table">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.label} scope="col" className={column.align === 'right' ? 'oa-num' : undefined}>
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            {row.cells.map((cell, index) => (
              <td
                key={index}
                className={columns[index]?.align === 'right' ? 'oa-num' : 'truncate'}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
